// Amounts, numbers and dates printed client-side exactly as the server prints
// them beside it. The client never owns a format: the server resolves the
// regional format (users.locale on a page, documents.pdf_locale in a PDF —
// LocaleSupport::FormatLocale) and hands its data to a Stimulus value; these
// functions only replay it. Intl.NumberFormat / Intl.DateTimeFormat are NOT
// used here — their CLDR data moves with the browser and differs from
// rails-i18n's (Intl en-US writes "CZK 1,234.50", Rails "1,234.50 CZK"), so a
// live refresh would flip the format of a tile the server rendered.
//
// The server side, one helper per shape (app/helpers/application_helper.rb):
//   currency_format_data(code)  → formatAmount        (one known currency)
//   number_format_data          → formatMoney, formatDecimal (any currency)
//   date_format_data(format)    → formatDateWith
// The element around the controllers that print numbers (the document form,
// for its summary and item discounts; the manual-pairing results list, once
// for all its rows) embeds number_format_data as data-server-number-format;
// serverNumberFormatFor finds it.
// test/helpers/server_format_parity_test.rb writes the server's strings to
// test/fixtures/files/server_format_parity.json and server-format.util.test.js
// replays them, so a drift on either side fails a suite.

import { getCurrencyDecimals } from "./number-format.util"

const FALLBACK_FORMAT = { unit: "€", format: "%u%n", separator: ".", delimiter: ",", precision: 2 }

// Rounds half up on the decimal digits, as Rails does: toFixed rounds the
// binary double, so 1.005 would print 1.00 where the server prints 1.01.
const roundedFixed = (value, precision) =>
  (Math.round(Number(`${Math.abs(value)}e${precision}`)) / 10 ** precision).toFixed(precision)

// A key the server did not send stays unset rather than overriding the
// fallback with undefined — {} must print "€1,234.50", not "1undefined234".
const definedOnly = (object = {}) =>
  Object.fromEntries(Object.entries(object || {}).filter(([, value]) => value !== undefined && value !== null))

const groupDigits = (whole, delimiter) => whole.replace(/\B(?=(\d{3})+(?!\d))/g, delimiter)

// number_to_currency(amount, currency:) from the hash currency_format_data
// sent (unit, format, separator, delimiter, precision).
export function formatAmount(amount, currencyFormat) {
  const { unit, format, separator, delimiter, precision } = { ...FALLBACK_FORMAT, ...definedOnly(currencyFormat) }
  const fixed = roundedFixed(amount, precision)
  const [whole, fraction] = fixed.split(".")
  const grouped = groupDigits(whole, delimiter)
  const number = fraction ? `${grouped}${separator}${fraction}` : grouped
  const text = format.replace("%n", number).replace("%u", unit)
  // Rails prefixes a negative amount with "-" ahead of the whole string, and
  // prints a value that rounds to zero without one.
  return amount < 0 && Number(fixed) > 0 ? `-${text}` : text
}

// ApplicationHelper#currency_format_data's rule, applied to number_format_data:
// the unit is the language's symbol for the code, else the code itself, which
// then goes after the number (ISO_CODE_FORMAT); the precision is the
// currency's minor unit (getCurrencyDecimals mirrors CurrencyEnums).
export function currencyFormatFor(code, numberFormat) {
  const data = numberFormat || {}
  const currency = String(code || "EUR").toUpperCase()
  const unit = data.units?.[currency] ?? currency
  return definedOnly({
    unit,
    format: unit === currency ? (data.iso_format ?? "%n %u") : data.format,
    separator: data.separator,
    delimiter: data.delimiter,
    precision: getCurrencyDecimals(currency)
  })
}

export function formatMoney(amount, code, numberFormat) {
  return formatAmount(Number(amount || 0), currencyFormatFor(code, numberFormat))
}

// A plain number with the format locale's grouping — a rate, a chart label.
// Trailing zeros are dropped down to minimumFractionDigits.
export function formatDecimal(value, numberFormat = {}, { minimumFractionDigits = 0, maximumFractionDigits = 2 } = {}) {
  const numeric = Number(value || 0)
  const fixed = roundedFixed(numeric, maximumFractionDigits)
  let [whole, fraction = ""] = fixed.split(".")
  while (fraction.length > minimumFractionDigits && fraction.endsWith("0")) fraction = fraction.slice(0, -1)
  whole = groupDigits(whole, numberFormat.number_delimiter ?? ",")
  const number = fraction ? `${whole}${numberFormat.number_separator ?? "."}${fraction}` : whole
  return numeric < 0 && Number(fixed) > 0 ? `-${number}` : number
}

// A percentage in the format locale's shape — number_to_percentage's
// percentage_format ("23 %" in sk, "23%" in en).
export function formatPercent(value, numberFormat = {}, options = {}) {
  return (numberFormat.percentage_format || "%n%").replace("%n", formatDecimal(value, numberFormat, options))
}

// Parsed once per distinct JSON string, not once per printed cell: a PDF
// connects one controller per number, and every cell reads the same data.
// Callers only read the object.
const parsedNumberFormats = new Map()

// The server's number data embedded as data-server-number-format
// (ApplicationHelper#number_format_data): the nearest one around `element`,
// else the page's own (the PDF's hidden div, a sibling of every cell). null
// when there is none — the caller then has no server format to replay.
export function serverNumberFormatFor(element) {
  const host =
    element?.closest?.("[data-server-number-format]") ?? document.querySelector("[data-server-number-format]")
  const raw = host?.dataset.serverNumberFormat
  if (raw === undefined) return null
  if (!parsedNumberFormats.has(raw)) {
    let parsed = null
    try {
      parsed = JSON.parse(raw)
    } catch {
      parsed = null
    }
    parsedNumberFormats.set(raw, parsed)
  }
  return parsedNumberFormats.get(raw)
}

const pad = (n) => String(n).padStart(2, "0")

// The strftime directives the app's date and time formats use
// (config/locales/dates/*.yml, and every value of LocaleSupport::Cldr::DATE_FIELDS
// for a region's CLDR patterns — fi-FI's time is "%-H.%M").
const DIRECTIVES = {
  d: (date) => pad(date.getDate()),
  "-d": (date) => String(date.getDate()),
  e: (date) => String(date.getDate()).padStart(2, " "),
  m: (date) => pad(date.getMonth() + 1),
  "-m": (date) => String(date.getMonth() + 1),
  Y: (date) => String(date.getFullYear()),
  y: (date) => pad(date.getFullYear() % 100),
  B: (date, names) => names.month_names?.[date.getMonth() + 1] ?? "",
  b: (date, names) => names.abbr_month_names?.[date.getMonth() + 1] ?? "",
  A: (date, names) => names.day_names?.[date.getDay()] ?? "",
  a: (date, names) => names.abbr_day_names?.[date.getDay()] ?? "",
  H: (date) => pad(date.getHours()),
  "-H": (date) => String(date.getHours()),
  M: (date) => pad(date.getMinutes()),
  "-M": (date) => String(date.getMinutes()),
  "%": () => "%"
}

// l(date, format:) from date_format_data(format): the regional pattern with
// the text language's month and day names. A Date, or "YYYY-MM-DD" read as a
// local calendar date. "" for a missing or invalid date.
export function formatDateWith(value, dateFormat = {}) {
  const date = toLocalDate(value)
  if (!date) return ""
  const pattern = dateFormat.pattern || "%Y-%m-%d"
  return pattern.replace(/%(-?[a-zA-Z%])/g, (match, directive) => {
    const render = DIRECTIVES[directive]
    return render ? render(date, dateFormat) : match
  })
}

function toLocalDate(value) {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value
  const match = typeof value === "string" && value.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (match) return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12)
  return null
}
